import { useMemo } from 'react';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';

import { api } from '@services/apiService';
import {
  team as teamResource,
  teamResourceUsageLimits,
  teams,
} from '@utils/resources/teams';

import { USER_QUERY_KEY, userTeamsSelector, useUserQuery } from './user';

// Scope Types

/**
 * @type {'scope/Team'}
 */
const TEAM_SCOPE_TYPE = '__scope::Team';

// Query Definitions
/**
 * @param {string} teamUuid
 * @return {import('@tanstack/react-query').UseQueryOptions<{ team: import('@api/types').TeamObject> }}
 */
export const teamQuery = (teamUuid) => ({
  queryKey: [{ scope: TEAM_SCOPE_TYPE, teamUuid }],
  queryFn: async () =>
    api
      .get(teamResource(teamUuid), {
        headers: {
          accept: 'application/json;version=2',
        },
      })
      .json(),
});

/**
 * Keyed under the team, so invalidating the team (e.g. after an upgrade) also
 * refetches its limits.
 *
 * @param {string} teamUuid
 * @return {import('@tanstack/react-query').UseQueryOptions<import('@api/types').ResourceUsageLimits>}
 */
export const resourceUsageLimitsQuery = (teamUuid) => ({
  queryKey: [
    { scope: TEAM_SCOPE_TYPE, teamUuid, entity: 'resourceUsageLimits' },
  ],
  queryFn: async () => api.get(teamResourceUsageLimits(teamUuid)).json(),
});

// Query Selectors
/**
 * @param {{ team: import('@api/types').TeamObject }} teamData
 * @returns {import('@api/types').TeamObject}
 */
const teamSelector = (teamData) => teamData.team;

/**
 * @param {{ team: import('@api/types').TeamObject }} teamData
 * @returns {import('@api/types').TeamObject['accountStatus']}
 */
export const accountStatusSelector = (teamData) =>
  teamData.team.accountStatus ? teamData.team.accountStatus : 'pending';

// Query Hooks
/**
 * @template [T=import('@api/types').TeamObject]
 * @param {string} teamUuid
 * @param {import('@tanstack/react-query').UseQueryOptions<{ team: import('@api/types').TeamObject }, unknown, T>} [params]
 * @return {import('@tanstack/react-query').UseQueryResult<T, unknown>}
 */
export const useTeamQuery = (teamUuid, params) =>
  useQuery({
    ...teamQuery(teamUuid),
    ...params,
    select: params?.select ?? teamSelector,
    enabled: params?.enabled ?? true,
  });

/**
 * @template [T=import('@api/types').ResourceUsageLimits]
 * @param {string} teamUuid
 * @param {import('@tanstack/react-query').UseQueryOptions<import('@api/types').ResourceUsageLimits, unknown, T>} [params]
 * @return {import('@tanstack/react-query').UseQueryResult<T, unknown>}
 */
export const useResourceUsageLimitsQuery = (teamUuid, params) =>
  useQuery({
    ...resourceUsageLimitsQuery(teamUuid),
    ...params,
  });

// Mutation Definitions
const updateTeamMutation = (teamUuid) => ({
  mutationFn: async (params) =>
    api
      .put(teamResource(teamUuid), {
        json: params,
      })
      .json(),
});

const deleteTeamMutation = (teamUuid) => ({
  mutationFn: (params) =>
    api
      .delete(teamResource(teamUuid), {
        json: params,
      })
      .json(),
});

// Mutation Hooks
export function useUpdateTeamMutation(teamUuid) {
  const queryClient = useQueryClient();

  const onSettled = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [{ teamUuid }] }),
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY }),
    ]);
  };

  return useMutation({
    ...updateTeamMutation(teamUuid),
    onSettled,
  });
}

export function useDeleteTeamMutation(teamUuid) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    ...deleteTeamMutation(teamUuid),
    async onSuccess() {
      await Promise.all([
        queryClient.resetQueries({ queryKey: [{ teamUuid }] }),
        queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY }),
      ]);

      navigate('/');
    },
  });
}

export function useCreateTeam() {
  return useMutation({
    mutationFn: async ({ name }) => {
      return api
        .post(teams(), {
          json: { name },
        })
        .json();
    },
  });
}

export function useAllUserTeams() {
  const { data: teams } = useUserQuery({ select: userTeamsSelector });

  const allTeams = useQueries({
    queries: teams?.map((team) => teamQuery(team.uuid)) ?? [],
  });

  return useMemo(() => {
    const data = allTeams.flatMap((team) => team.data?.team ?? []);
    const isLoading = allTeams.some((team) => team.isLoading);

    return { data, isLoading };
  }, [allTeams]);
}

const enterpriseFeaturesSelector = (teamData) =>
  teamData.team.enterpriseFeatures ?? [];

/**
 * @param {string} feature
 * @returns {boolean}
 */
export function useTeamEnterpriseFeature(feature) {
  const { teamUuid } = useParams();
  const { data: features = [] } = useTeamQuery(teamUuid, {
    select: enterpriseFeaturesSelector,
  });

  return features.includes(feature);
}
